import { doc, getDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import {
    FaFacebookF,
    FaLinkedinIn,
    FaShareAlt,
    FaTwitter,
    FaWhatsapp,
} from "react-icons/fa";
import { MdArrowBackIosNew } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import {
    FacebookShareButton,
    LinkedinShareButton,
    TwitterShareButton,
    WhatsappShareButton,
} from "react-share";
import { Container, Segment, Table } from "semantic-ui-react";
import { db } from "../firebase"; // Ensure you import your Firebase config
import SEO from "../SEO";
import "./DemoPage.css";
import CustomModal from "./Modal";

import { FiShoppingCart } from "react-icons/fi";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import GlobalCTA from "./../components/GlobalCTA";

const DemoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [modalShow, setModalShow] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const baseUrl = "https://www.oglitz.com";

  useEffect(() => {
    const fetchPackageData = async () => {
      try {
        const docRef = doc(db, "designPackages", id);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setPackageData(docSnap.data());
        } else {
          setError("No such document!");
        }
      } catch (err) {
        setError("Error fetching data.");
        console.error("Error fetching document:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageData();
  }, [id]);

  const openRequestModal = (data) => {
    setModalData(data);
    setModalShow(true);
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!packageData) return <div>No package data found!</div>;

  // Share URL
  const shareUrl = `${baseUrl}${packageData.link}`; // The current URL

  // Schema Data for structured data
  const schemaPackages = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: packageData.title,
    description: packageData.description,
    benefits: packageData.benefits,
    moreInfo: packageData.moreInfo,
    keyFeatures: packageData.keyFeatures,
    image: packageData.logo[0],
    offers: {
      "@type": "Offer",
      priceCurrency: "INR",
      price: packageData.price,
      url: `${baseUrl}${packageData.link}`,
      itemCondition: "https://schema.org/NewCondition",
      availability: "https://schema.org/InStock",
    },
    includedInPackage: packageData.whatsIncluded.map((item) => ({
      "@type": "PropertyValue",
      name: item.feature,
      value: item.detail,
    })),
    demoImages: packageData.logo.map((url) => ({
      "@type": "ImageObject",
      url: url,
    })),
  };

  // Dynamic title with packageData.title
  const seoTitle = packageData.title
    ? `${packageData.title} | Oglitz Software Pvt Ltd.`
    : "Design Packages | Oglitz Software Pvt Ltd.";

  return (
    <>
      <Container className="oglitz-demo-page">
        <SEO
          title={seoTitle}
          description={`Discover the premium design packages offered by Oglitz Software Pvt Ltd., including logo design, branding, and social media assets. Our packages are tailored to meet the needs of businesses looking to enhance their brand identity. Request a quote today to get started!`}
          image={packageData.logo[0]} // Update with your actual image URL
          url={`${baseUrl}${packageData.link}`}
          keywords="design packages, logo design, branding, social media design, graphic design, business branding, professional design services, Oglitz Software Pvt Ltd., Oglitz Software"
          author="Oglitz Software Pvt Ltd."
          twitterHandle="@oglitz"
          schemaData={schemaPackages}
        />

        {/* HERO */}
        <div className="oglitz-demo-hero__top">
          <button className="oglitz-demo-back" onClick={() => navigate(-1)}>
            <MdArrowBackIosNew />
            <span>Back</span>
          </button>

          <div className="oglitz-demo-actions">
            <div className="oglitz-demo-share">
              <button
                className="oglitz-demo-share-btn"
                onClick={() =>
                  document
                    .getElementById("share-options")
                    .classList.toggle("visible")
                }
              >
                <FaShareAlt />
              </button>

              <div id="share-options" className="share-options">
                <FacebookShareButton url={shareUrl}>
                  <FaFacebookF />
                </FacebookShareButton>
                <LinkedinShareButton url={shareUrl}>
                  <FaLinkedinIn />
                </LinkedinShareButton>
                <TwitterShareButton url={shareUrl}>
                  <FaTwitter />
                </TwitterShareButton>
                <WhatsappShareButton url={shareUrl}>
                  <FaWhatsapp />
                </WhatsappShareButton>
              </div>
            </div>

            <button
              className="oglitz-demo-order-btn"
              onClick={() => openRequestModal(packageData)}
            >
              <FiShoppingCart />
              Order Now
            </button>
          </div>
        </div>

        {/* TITLE BELOW */}
        <h1 className="oglitz-demo-title-mobile">{packageData.title}</h1>
        <div className="oglitz-demo-meta">
          <span className="oglitz-demo-badge price">₹{packageData.price}</span>
          <span className="oglitz-demo-badge time">{packageData.duration}</span>
        </div>
        {/* IMAGE GALLERY */}
        <Segment className="demo-gallery-section">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            spaceBetween={20}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            autoplay={{ delay: 3500 }}
            breakpoints={{
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {packageData.logo.map((img, index) => (
              <SwiperSlide key={index}>
                <div className="demo-gallery-card">
                  <img
                    src={img}
                    alt={`${packageData.title} design ${index + 1}`}
                  />
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </Segment>

        {/* DETAILS */}
        <section className="oglitz-demo-details-flow">
          <div className="oglitz-demo-detail-item">
            <h3 className="oglitz-demo-detail-title">Description</h3>
            <p className="oglitz-demo-detail-text">{packageData.description}</p>
          </div>

          <div className="oglitz-demo-detail-item">
            <h3 className="oglitz-demo-detail-title">Benefits</h3>
            <p className="oglitz-demo-detail-text">{packageData.benefits}</p>
          </div>

          <div className="oglitz-demo-detail-item">
            <h3 className="oglitz-demo-detail-title">Key Features</h3>
            <p className="oglitz-demo-detail-text">{packageData.keyFeatures}</p>
          </div>

          <div className="oglitz-demo-detail-item">
            <h3 className="oglitz-demo-detail-title">More Info</h3>
            <p className="oglitz-demo-detail-text">{packageData.moreInfo}</p>
          </div>
        </section>

        {/* INCLUDED TABLE */}
        <section className="oglitz-demo-included oglitz-demo-included--left">
          <h2 className="oglitz-demo-included-title">
            What’s <span>Included</span>
          </h2>

          <div className="oglitz-demo-table-wrap">
            <Table celled className="oglitz-demo-table">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Feature</Table.HeaderCell>
                  <Table.HeaderCell>Detail</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {packageData.whatsIncluded.map((item, i) => (
                  <Table.Row key={i}>
                    <Table.Cell className="oglitz-feature-cell">
                      {item.feature}
                    </Table.Cell>
                    <Table.Cell className="oglitz-detail-cell">
                      {item.detail}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </section>

        <CustomModal
          isOpen={modalShow}
          onRequestClose={() => setModalShow(false)}
          modalData={modalData}
        />
      </Container>
      <GlobalCTA />
    </>
  );
};

export default DemoPage;
